import { WidgetActionMenu, WidgetActionMenuItem } from '@/ui'
import { InvoicesIcon } from '@/ui/views/settings/settings-nav-icons'
import { CreditCardIcon } from '@heroicons/react/24/solid'
import { CardPaymentMethodDetails, PaymentMethodData, SepaPaymentMethodDetails } from '@jobilla/nexus-sdk'
import { LandmarkIcon, PencilIcon, StarIcon, Trash2Icon } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

export const isCardDetails = (details: PaymentMethodData['details']): details is CardPaymentMethodDetails =>
	'brand' in details

export const isSepaDetails = (details: PaymentMethodData['details']): details is SepaPaymentMethodDetails =>
	'country' in details

// Stripe sends the brand as a lowercase id.
const CARD_BRAND_NAMES: Record<string, string> = {
	amex: 'American Express',
	cartes_bancaires: 'Cartes Bancaires',
	diners: 'Diners Club',
	discover: 'Discover',
	jcb: 'JCB',
	mastercard: 'Mastercard',
	unionpay: 'UnionPay',
	visa: 'Visa',
}

export function cardBrandName(brand: string): string {
	return CARD_BRAND_NAMES[brand] ?? brand
}

export function describePaymentMethod(pm: PaymentMethodData, t: (key: string) => string): string {
	if (pm.nickname) {
		return pm.nickname
	}
	if (isCardDetails(pm.details)) {
		return `${cardBrandName(pm.details.brand)} •••• ${pm.details.last4}`
	}
	return `${t('settings.paymentMethods.sepaLabel')} •••• ${pm.details.last4}`
}

interface PaymentMethodsListProps {
	rows: PaymentMethodData[]
	canSetDefault: boolean
	canUpdate: boolean
	canDelete: boolean
	onSetDefault: (paymentMethod: PaymentMethodData) => void
	onEditNickname: (paymentMethod: PaymentMethodData) => void
	onDelete: (paymentMethod: PaymentMethodData) => void
	isRowMutating: (paymentMethodId: string) => boolean
}

export function PaymentMethodsList(
	{
		rows,
		canSetDefault,
		canUpdate,
		canDelete,
		onSetDefault,
		onEditNickname,
		onDelete,
		isRowMutating,
	}: PaymentMethodsListProps,
) {
	return (
		<div className=" overflow-hidden">
			{rows.map((pm) => (
				<PaymentMethodRow
					key={pm.id}
					paymentMethod={pm}
					canSetDefault={canSetDefault}
					canUpdate={canUpdate}
					canDelete={canDelete}
					onSetDefault={onSetDefault}
					onEditNickname={onEditNickname}
					onDelete={onDelete}
					isMutating={isRowMutating(pm.id)}
				/>
			))}
		</div>
	)
}

interface InvoicePaymentMethodRowProps {
	isPrimary: boolean
	canSetDefault: boolean
	onSetDefault: () => void
	isMutating: boolean
}

export function InvoicePaymentMethodRow(
	{ isPrimary, canSetDefault, onSetDefault, isMutating }: InvoicePaymentMethodRowProps,
) {
	const { t } = useTranslation('common')

	return (
		<div className="group flex items-center gap-6 py-4 bg-white transition-colors hover:bg-grey-50/40">
			<div className="flex items-center gap-3 flex-1 min-w-0">
				<InvoicesIcon className="w-5 h-5 text-grey-500 shrink-0" />
				<div className="flex flex-col min-w-0 leading-tight">
					<span className="text-sm font-semibold text-grey-900">
						{t('settings.paymentMethods.invoiceLabel')}
					</span>
					<span className="text-xs text-grey-500 mt-0.5">
						{t('settings.paymentMethods.invoiceDescription')}
					</span>
				</div>
			</div>
			{isPrimary
				? (
					<span className="flex items-center gap-1.5 text-xs font-medium text-brand-700 shrink-0">
						<span className="w-1.5 h-1.5 rounded-full bg-brand-500" />
						{t('profiles.primary')}
					</span>
				)
				: null}
			{isPrimary
				? <span className="size-8 shrink-0" />
				: (
					<WidgetActionMenu
						items={[{
							id: 'make-primary',
							label: t('settings.paymentMethods.makePrimary'),
							icon: <StarIcon className="size-4" />,
							onSelect: onSetDefault,
							disabled: isMutating || !canSetDefault,
							tooltip: canSetDefault ? undefined : t('settings.paymentMethods.adminOnly'),
						}]}
						ariaLabel={t('settings.paymentMethods.rowMenu')}
					/>
				)}
		</div>
	)
}

interface PaymentMethodRowProps {
	paymentMethod: PaymentMethodData
	canSetDefault: boolean
	canUpdate: boolean
	canDelete: boolean
	onSetDefault: (paymentMethod: PaymentMethodData) => void
	onEditNickname: (paymentMethod: PaymentMethodData) => void
	onDelete: (paymentMethod: PaymentMethodData) => void
	isMutating: boolean
}

function PaymentMethodRow(
	{
		paymentMethod,
		canSetDefault,
		canUpdate,
		canDelete,
		onSetDefault,
		onEditNickname,
		onDelete,
		isMutating,
	}: PaymentMethodRowProps,
) {
	const { t } = useTranslation('common')
	const showSetDefault = !paymentMethod.isDefault
	const adminOnly = t('settings.paymentMethods.adminOnly')

	const menuItems: WidgetActionMenuItem[] = [
		...(showSetDefault
			? [{
				id: 'make-primary',
				label: t('settings.paymentMethods.makePrimary'),
				icon: <StarIcon className="size-4" />,
				onSelect: () => onSetDefault(paymentMethod),
				disabled: isMutating || !canSetDefault,
				tooltip: canSetDefault ? undefined : adminOnly,
			} satisfies WidgetActionMenuItem]
			: []),
		{
			id: 'edit-nickname',
			label: t('settings.paymentMethods.editNickname.action'),
			icon: <PencilIcon className="size-4" />,
			onSelect: () => onEditNickname(paymentMethod),
			disabled: isMutating || !canUpdate,
			tooltip: canUpdate ? undefined : adminOnly,
		},
		{
			id: 'remove',
			label: t('settings.paymentMethods.delete.confirm'),
			icon: <Trash2Icon className="size-4" />,
			variant: 'danger',
			onSelect: () => onDelete(paymentMethod),
			disabled: isMutating || !canDelete,
			tooltip: canDelete ? undefined : adminOnly,
		},
	]

	return (
		<div className="group flex items-center gap-6 py-4 bg-white transition-colors hover:bg-grey-50/40">
			<MethodIdentity paymentMethod={paymentMethod} />
			<div className="flex flex-col items-end gap-1">
				{paymentMethod.isDefault
					? (
						<span className="flex items-center gap-1.5 text-xs font-medium text-brand-700 shrink-0">
							<span className="w-1.5 h-1.5 rounded-full bg-brand-500" />
							{t('profiles.primary')}
						</span>
					)
					: null}
				<MethodDetail paymentMethod={paymentMethod} />
			</div>
			<WidgetActionMenu items={menuItems} ariaLabel={t('settings.paymentMethods.rowMenu')} />
		</div>
	)
}

function MethodIdentity({ paymentMethod }: { paymentMethod: PaymentMethodData }) {
	const { t } = useTranslation('common')

	const icon = isCardDetails(paymentMethod.details)
		? <CreditCardIcon className="w-5 h-5 text-grey-500 shrink-0" title={paymentMethod.details.brand} />
		: <LandmarkIcon className="w-5 h-5 text-grey-500 shrink-0" />

	const typeLabel = isCardDetails(paymentMethod.details)
		? <span className="capitalize">{paymentMethod.details.brand}</span>
		: <span>{t('settings.paymentMethods.sepaLabel')}</span>

	const last4 = isCardDetails(paymentMethod.details) || isSepaDetails(paymentMethod.details)
		? paymentMethod.details.last4
		: null

	if (last4 === null) {
		return null
	}

	if (paymentMethod.nickname) {
		return (
			<div className="flex items-center gap-3 flex-1 min-w-0">
				{icon}
				<div className="flex flex-col min-w-0 leading-tight">
					<span className="text-sm font-semibold text-grey-900 truncate">
						{paymentMethod.nickname}
					</span>
					<div className="flex items-center gap-2 text-xs text-grey-500 mt-0.5">
						{typeLabel}
						<span className="tracking-widest">&bull;&bull;&bull;&bull; {last4}</span>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className="flex items-center gap-3 flex-1 min-w-0">
			{icon}
			<span className="capitalize font-medium text-grey-900">{typeLabel}</span>
			<span className="text-grey-600 tracking-widest">&bull;&bull;&bull;&bull; {last4}</span>
		</div>
	)
}

function MethodDetail({ paymentMethod }: { paymentMethod: PaymentMethodData }) {
	const { t, i18n } = useTranslation('common')
	const regionFormatter = useMemo(
		() => new Intl.DisplayNames(i18n.language, { type: 'region' }),
		[i18n.language],
	)

	if (isCardDetails(paymentMethod.details)) {
		return (
			<DetailStat
				label={t('settings.paymentMethods.expiresLabel')}
				value={`${paymentMethod.details.expMonth.toString().padStart(2, '0')}/${paymentMethod.details.expYear}`}
			/>
		)
	}

	if (isSepaDetails(paymentMethod.details)) {
		const country = (() => {
			try {
				return regionFormatter.of(paymentMethod.details.country) ?? paymentMethod.details.country
			} catch {
				return paymentMethod.details.country
			}
		})()
		return <DetailStat label={t('settings.paymentMethods.countryLabel')} value={country} />
	}

	return null
}

function DetailStat({ label, value }: { label: string; value: string }) {
	return (
		<div className="space-y-0.5 text-right">
			<div className="text-sm font-semibold text-grey-700 tabular-nums leading-tight">
				{label} {value}
			</div>
		</div>
	)
}
